import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  RCS_ACCEPT_STATUS_ACCEPTED,
  RCS_CONVERSATION_STATUS_ACTIVE,
} from '@rsos/constants/rcsStatuses';
import useTF911LoggedInUser from '@rsos/hooks/talkjs/useTF911LoggedInUser';
import { useOpenChatTypes } from '@rsos/reduxDataStore/src/features/chats/hooks';
import { CHAT_TAB } from '@rsos/reduxDataStore/src/features/eventPane/eventPaneTabs';
import { useLazyIndicateTypingRcsConversationQuery } from '@rsos/reduxDataStore/src/features/rcs/rcsApi';
import { useSelectedRcsConversation } from '@rsos/reduxDataStore/src/features/rcs/rcsSelectors';
import {
  setRcsUnseenMessageStatus,
  setRcsunblurMessageUrls,
} from '@rsos/reduxDataStore/src/features/rcs/rcsSlice';
import { selectUserOrgByApp } from '@rsos/sinatra';
import { createTalkUser } from '@rsos/utils/talkJS';
import { getTalkInstances, useTalkJS } from '@rsos/utils/useTalkJS';
import { RootState } from '../../../../app';
import useHasAccessToUniteEventPane from '../../../../hooks/useHasAccessToUniteEventPane';
import useIsVT911Enabled from '../../../../hooks/useIsVT911Enabled';
import useTalkJSTheme from '../../../../hooks/useTalkJSTheme';
import { ChatContainer } from './RcsChat.styles';
import { ICON_ROW_HEIGHT } from './RcsChatBox';
import {
  RCS_CHAT_HEADER_HEIGHT,
  RCS_TYPING_INDICATOR_WINDOW,
} from './constants';

interface RCSChatInterface {
  callerID: string;
  showButtons: boolean;
  isRcsChatBoxVisible: boolean;
}

const RcsChat = ({
  callerID,
  showButtons,
  isRcsChatBoxVisible,
}: RCSChatInterface) => {
  const dispatch = useDispatch();
  const loggedInUser = useTF911LoggedInUser();
  const chatTheme = useTalkJSTheme(false, true);

  const { shellRef } = useTalkJS({
    theme: chatTheme,
    user: loggedInUser,
    instanceID: 'main',
  });
  const { chat, session } = getTalkInstances(chatTheme);

  const selectedTab = useSelector(
    (state: RootState) => state.eventPane.selectedTab,
  );
  const isRcsChatVisibleRedux = useOpenChatTypes().rcs;

  const [hasStartedTyping, setHasStartedTyping] = useState(false);
  const lastTriggeredRef = useRef<number | null>(null);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isVT911Enabled = useIsVT911Enabled();
  const rcsConversation = useSelectedRcsConversation(callerID);
  const hasAccessToUniteEventPane = useHasAccessToUniteEventPane();

  const orgName = useSelector(
    (state: RootState) =>
      selectUserOrgByApp(
        state.sinatra,
        state.sinatra.user.currentRole?.application || '',
      )?.name,
  );

  const unblurMessageUrls = useSelector(
    (state: RootState) => state.rcs.unblurMessageUrls,
  );

  const [triggerIndicateTypingRcsConversation] =
    useLazyIndicateTypingRcsConversationQuery();

  const rcsConversationId = useMemo(() => {
    if (!rcsConversation || !rcsConversation.data) return;
    return rcsConversation.data.conversation_id;
  }, [rcsConversation]);

  const shouldDisplayMessageField = useMemo(() => {
    const isAccepted =
      rcsConversation?.data?.accept_status === RCS_ACCEPT_STATUS_ACCEPTED;
    const isActive =
      rcsConversation?.data?.conversation_status ===
      RCS_CONVERSATION_STATUS_ACTIVE;
    return isAccepted && isActive;
  }, [rcsConversation]);

  const triggerTypingIndicator = useCallback(() => {
    const now = Date.now();
    if (
      lastTriggeredRef.current &&
      now - lastTriggeredRef.current >= RCS_TYPING_INDICATOR_WINDOW
    ) {
      lastTriggeredRef.current = now;
      triggerIndicateTypingRcsConversation({ callerID, orgName });
    }
  }, [callerID, orgName, triggerIndicateTypingRcsConversation]);

  const handleTyping = useCallback(() => {
    if (!hasStartedTyping) {
      setHasStartedTyping(true);
      triggerTypingIndicator();
    }

    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    typingTimeoutRef.current = setTimeout(() => {
      setHasStartedTyping(false);
      typingTimeoutRef.current = null;
    }, RCS_TYPING_INDICATOR_WINDOW);
  }, [triggerTypingIndicator, hasStartedTyping]);

  useEffect(() => {
    if (chat && session && rcsConversationId) {
      const conversation = session.getOrCreateConversation(rcsConversationId);
      conversation.setParticipant(createTalkUser(loggedInUser));

      conversation.setAttributes({
        custom: { unblurMessageUrls: JSON.stringify(unblurMessageUrls) },
      });

      chat.select(conversation);
    }
  }, [session, chat, rcsConversationId, loggedInUser, unblurMessageUrls]);

  useEffect(() => {
    if (chat && rcsConversationId) {
      chat.messageField.setVisible(shouldDisplayMessageField);

      chat.onBlur(() => {
        chat.setPresence({ visible: false });
      });

      chat.onFocus(() => {
        chat.setPresence({ visible: true });
      });

      chat.onKeydown(e => {
        if (e.key !== 'Enter') {
          handleTyping();
        }
      });

      chat.onCustomMessageAction('toggleBlur', event => {
        const attachment = event?.message?.attachment;
        if (!attachment) return;

        if (unblurMessageUrls.includes(attachment.url)) {
          dispatch(
            setRcsunblurMessageUrls({
              unblurMessageUrls: unblurMessageUrls.filter(
                (url: string) => url !== url,
              ),
            }),
          );
        } else {
          dispatch(
            setRcsunblurMessageUrls({
              unblurMessageUrls: [...unblurMessageUrls, attachment.url],
            }),
          );
        }
      });
    }
  }, [
    session,
    chat,
    rcsConversationId,
    loggedInUser,
    shouldDisplayMessageField,
    handleTyping,
    unblurMessageUrls,
    dispatch,
  ]);

  // Get separate unread tracking to check if we need to clear unread status
  const rcsUnreadMessages = useSelector(
    (state: RootState) => state.rcs?.unreadMessages || {},
  );
  const hasUnreadMessage =
    rcsUnreadMessages[callerID] || rcsConversation?.ui?.unseenMessage;

  useEffect(() => {
    // use redux state for visibility, but fall back to prop for backward compatibility
    const chatVisible =
      isRcsChatVisibleRedux !== undefined
        ? isRcsChatVisibleRedux
        : isRcsChatBoxVisible;

    if (
      selectedTab === CHAT_TAB &&
      chatVisible &&
      rcsConversationId &&
      session &&
      chat &&
      isRcsChatBoxVisible &&
      hasUnreadMessage
    ) {
      dispatch(setRcsUnseenMessageStatus({ callerID, unseenMessage: false }));
    }
  }, [
    selectedTab,
    isRcsChatVisibleRedux,
    rcsConversationId,
    session,
    chat,
    isRcsChatBoxVisible,
    hasUnreadMessage,
    rcsUnreadMessages,
    rcsConversation,
    callerID,
    dispatch,
  ]);

  const containerMaxHeight = useMemo(() => {
    if (showButtons && !rcsConversationId) {
      return 0;
    } else if (showButtons && isVT911Enabled) {
      if (hasAccessToUniteEventPane) {
        return `calc(100% - ${RCS_CHAT_HEADER_HEIGHT}}px)`;
      }
      return `calc(100% - ${RCS_CHAT_HEADER_HEIGHT} - ${ICON_ROW_HEIGHT}px)`;
    }
  }, [
    rcsConversationId,
    hasAccessToUniteEventPane,
    isVT911Enabled,
    showButtons,
  ]);

  return (
    <ChatContainer
      height="100%"
      maxHeight={containerMaxHeight}
      overflowY={!isRcsChatBoxVisible ? 'hidden' : 'auto'}
      showBorder={isRcsChatBoxVisible && rcsConversationId}
      ref={shellRef}
    />
  );
};
export default RcsChat;
